import type { FontId, PortalTheme } from './schema'

/** Same keys as ClientPortal's `BrandTokens` (theme-layer `useTheme`). */
export type BrandTokenOverrides = Partial<
  Record<'primary' | 'secondary' | 'tertiary' | 'background' | 'surface' | 'error' | 'fontColor' | 'sidebar' | 'border', string>
>

const SEED_TO_TOKEN = {
  primary: 'primary',
  secondary: 'secondary',
  accent: 'tertiary',
  background: 'background',
  surface: 'surface',
  destructive: 'error',
  foreground: 'fontColor',
  sidebar: 'sidebar',
  border: 'border',
} as const

/** Light colour seeds → ClientPortal brand tokens. Seeds without a token (muted, success, …) are not applied yet. */
export function toBrandTokens(theme: PortalTheme | undefined): BrandTokenOverrides {
  const seeds = theme?.colors?.light
  const tokens: BrandTokenOverrides = {}
  if (!seeds) return tokens
  for (const [seed, token] of Object.entries(SEED_TO_TOKEN) as [keyof typeof SEED_TO_TOKEN, keyof BrandTokenOverrides][]) {
    const color = seeds[seed]
    if (color) tokens[token] = color
  }
  return tokens
}

/** Font id → [Google Fonts family (null = system font), generic fallback, weights]. */
const FONTS: Record<FontId, [family: string | null, generic: 'sans-serif' | 'serif', weights: string]> = {
  inter: ['Inter', 'sans-serif', '400;500;600;700'],
  lato: ['Lato', 'sans-serif', '400;700'],
  montserrat: ['Montserrat', 'sans-serif', '400;500;600;700'],
  roboto: ['Roboto', 'sans-serif', '400;500;600;700'],
  poppins: ['Poppins', 'sans-serif', '400;500;600;700'],
  'dm-sans': ['DM Sans', 'sans-serif', '400;500;600;700'],
  'plus-jakarta-sans': ['Plus Jakarta Sans', 'sans-serif', '400;500;600;700'],
  'space-grotesk': ['Space Grotesk', 'sans-serif', '400;500;600;700'],
  'playfair-display': ['Playfair Display', 'serif', '400;500;600;700'],
  'source-serif-4': ['Source Serif 4', 'serif', '400;500;600;700'],
  lora: ['Lora', 'serif', '400;500;600;700'],
  fraunces: ['Fraunces', 'serif', '400;500;600;700'],
  'system-sans': [null, 'sans-serif', ''],
}

type Fonts = PortalTheme['fonts']

/** CSS `font-family` value for a font id. */
function fontStack(fontId: FontId): string {
  const [family, generic] = FONTS[fontId]
  return family ? `"${family}", system-ui, ${generic}` : `system-ui, -apple-system, "Segoe UI", ${generic}`
}

/**
 * Global CSS for the chosen fonts, as `--cp-font-body` / `--cp-font-heading` variables
 * (body font also drives HighRise's `--hr-font-family-base`); null when no font is chosen.
 * Selectors outrank the app's `html, body` / `:root` defaults regardless of stylesheet order.
 */
export function fontStyles(fonts: Fonts): string | null {
  const body = fonts?.body && fontStack(fonts.body)
  const heading = fonts?.heading && fontStack(fonts.heading)
  const css = [
    body && `:root:root{--cp-font-body:${body};--hr-font-family-base:${body}}html body{font-family:var(--cp-font-body)}`,
    heading && `:root:root{--cp-font-heading:${heading}}h1,h2,h3,h4,h5,h6{font-family:var(--cp-font-heading)}`,
  ]
  return css.filter(Boolean).join('') || null
}

/** Google Fonts css2 stylesheet for the chosen non-system fonts; null when none need loading. */
export function fontStylesheetHref(fonts: Fonts): string | null {
  const families = [...new Set([fonts?.heading, fonts?.body])].flatMap(fontId => {
    const [family, , weights] = (fontId && FONTS[fontId]) || []
    return family ? [`family=${family.replaceAll(' ', '+')}:wght@${weights}`] : []
  })
  return families.length ? `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap` : null
}
